import React from 'react';
// File is *Form so webpack does not collide with researchPersonIntake.ts on case-insensitive disks.
import { OfacLocationSelect, type OfacLocationValue } from './OfacLocationSelect';
import { OfacNameOrderIntake, type OfacNameOrderValue } from './OfacNameOrderIntake';
import {
  applyOfacNameOrderEdit,
  ofacAddressFieldLabel,
  ofacAddressLayout,
  ofacCountryOptions,
  ofacNationalIdTypes,
  withOtherOption,
  type OfacLanguage,
} from './ofacIntakeCatalog';
import {
  applyResearchAgeBasisChange,
  applyResearchGenderChange,
  applyResearchNationalitiesChange,
  applyResearchSearchCountryChange,
  composeResearchPersonFormerName,
  composeResearchPersonLegalName,
  composeResearchPersonPlace,
  researchPersonAgeBasis,
  researchPersonFormerNameStyle,
  researchPersonIdCountry,
  researchPersonNameStyle,
  researchPersonNationalityCodes,
  researchPersonSearchCountry,
  clearResearchPersonFormerNameFields,
  researchPersonShowsMaidenName,
  researchPersonVisibleFields,
  type ResearchPersonAgeBasis,
  type ResearchPersonKeyFields,
} from './researchPersonIntake';
import {
  ageRangeDraftParts,
  ageRangeIsTooWide,
  parseAgeRange,
  updateAgeRangeDraft,
} from './researchAgeRange';

export type ResearchPersonKeyData = ResearchPersonKeyFields & {
  names: string;
  addresses: string;
  birthdate: string;
  ageRange: string;
  ageBasis?: ResearchPersonAgeBasis;
  ssn: string;
  dlNumber: string;
  nationalIdType: string;
  nationalId: string;
  knownAliases: string;
};

export type ResearchPersonIntakeSection = 'identity' | 'identifiers';

type ResearchPersonIntakeProps<T extends ResearchPersonKeyData> = {
  language: OfacLanguage;
  keyData: T;
  onKeyDataChange: (next: T) => void;
  fieldProps: (field: string) => Record<string, unknown>;
  sections?: ResearchPersonIntakeSection[];
};

export function ResearchPersonIntake<T extends ResearchPersonKeyData>({
  language,
  keyData,
  onKeyDataChange,
  fieldProps,
  sections = ['identity', 'identifiers'],
}: ResearchPersonIntakeProps<T>) {
  const searchCountry = researchPersonSearchCountry(keyData);
  const nationalityCodes = researchPersonNationalityCodes(keyData);
  const visible = researchPersonVisibleFields(searchCountry, nationalityCodes[0] || '');
  const showIdentity = sections.includes('identity');
  const showIdentifiers = sections.includes('identifiers');
  const countryOptions = withOtherOption(ofacCountryOptions(language), language);
  const idCountry = researchPersonIdCountry(keyData);
  const idTypes = ofacNationalIdTypes(idCountry);
  const nameStyle = researchPersonNameStyle(keyData);
  const formerNameStyle = researchPersonFormerNameStyle(keyData);
  const nameDetectHints = {
    nationalities: nationalityCodes,
    searchCountry,
  };
  const formerOrigin = nationalityCodes.find((code) => code !== 'US' && code !== 'USA') || '';

  const patch = (partial: Partial<T>) => {
    onKeyDataChange({ ...keyData, ...partial });
  };

  const nameValue: OfacNameOrderValue = {
    nameStyle,
    givenName: keyData.givenName || '',
    givenName2: keyData.givenName2 || '',
    surname: keyData.surname || '',
    surname2: keyData.surname2 || '',
    entityName: keyData.names || composeResearchPersonLegalName(keyData),
  };

  const locationValue: OfacLocationValue = {
    country: keyData.locationCountry || keyData.searchCountry || keyData.nationality,
    otherCountry: keyData.locationOtherCountry,
    region: keyData.locationRegion,
    otherRegion: keyData.locationOtherRegion,
    county: keyData.locationCounty,
    otherCounty: keyData.locationOtherCounty,
    city: keyData.locationCity,
    otherCity: keyData.locationOtherCity,
  };

  return (
    <>
      {showIdentity ? (
        <div className="research-intake-section">
          <h4 className="research-intake-section-title">
            {language === 'es' ? 'Nombre legal actual' : 'Current legal name'}
          </h4>
          <p className="research-form-panel-help">
            {language === 'es'
              ? 'Usa el nombre legal de hoy. Si eliges mujer abajo, puedes optar por añadir un apellido de soltera.'
              : 'Use the current legal name. If you select Female below, you can opt in to add a maiden name.'}
          </p>
          <OfacNameOrderIntake
            language={language}
            fieldProps={fieldProps}
            detected={!keyData.nameStyleLocked}
            value={nameValue}
            onChange={(next) => {
              const edited = applyOfacNameOrderEdit(nameValue, next, {
                detect: true,
                locked: Boolean(keyData.nameStyleLocked),
                hints: nameDetectHints,
              });
              onKeyDataChange({
                ...keyData,
                nameStyle: edited.value.nameStyle,
                nameStyleLocked: edited.locked,
                givenName: edited.value.givenName,
                givenName2: edited.value.givenName2,
                surname: edited.value.surname,
                surname2: edited.value.surname2,
                names: edited.value.entityName,
              } as T);
            }}
          />
          <fieldset className="research-intake-target-type research-intake-gender">
            <legend>{language === 'es' ? 'Sexo' : 'Gender'}</legend>
            <label>
              <input
                type="radio"
                name={String((fieldProps('gender') as { name?: string }).name || 'research-person-gender')}
                checked={String(keyData.gender || '') === 'male'}
                onChange={() => onKeyDataChange(applyResearchGenderChange(keyData, 'male'))}
              />
              {language === 'es' ? 'Hombre' : 'Male'}
            </label>
            <label>
              <input
                type="radio"
                name={String((fieldProps('gender') as { name?: string }).name || 'research-person-gender')}
                checked={String(keyData.gender || '') === 'female'}
                onChange={() => onKeyDataChange(applyResearchGenderChange(keyData, 'female'))}
              />
              {language === 'es' ? 'Mujer' : 'Female'}
            </label>
          </fieldset>
          {String(keyData.gender || '') === 'female' ? (
            <label className="dashboard-checkbox-row research-intake-maiden-opt">
              <input
                type="checkbox"
                checked={Boolean(keyData.includeMaidenName)}
                onChange={(event) => {
                  if (event.target.checked) {
                    patch({ includeMaidenName: true } as Partial<T>);
                    return;
                  }
                  onKeyDataChange(clearResearchPersonFormerNameFields({
                    ...keyData,
                    includeMaidenName: false,
                  }) as T);
                }}
              />
              <span>{language === 'es' ? 'Ingresar nombre anterior / de soltera' : 'Enter former / maiden name'}</span>
            </label>
          ) : null}
          <div className="research-intake-nested">
            {researchPersonShowsMaidenName(keyData) ? (
              <>
                <h5 className="research-intake-nested-title">
                  {language === 'es' ? 'Nombre anterior / de soltera' : 'Former / maiden name'}
                </h5>
                <p className="research-form-panel-help">
                  {language === 'es'
                    ? 'Usa el orden con el que se escribió ese nombre (dos apellidos hispanos o un apellido estadounidense). Vantage lo busca como alias; el nombre legal actual sigue siendo el principal.'
                    : 'Use the naming custom that name was written under (Spanish two surnames vs one US last name). Vantage searches it as an alias; the current legal name stays primary.'}
                </p>
                <OfacNameOrderIntake
                  compact
                  fieldPrefix={language === 'es' ? 'Anterior: ' : 'Former '}
                  language={language}
                  detected={!keyData.formerNameStyleLocked}
                  value={{
                    nameStyle: formerNameStyle,
                    givenName: keyData.formerGivenName || '',
                    givenName2: keyData.formerGivenName2 || '',
                    surname: keyData.formerSurname || '',
                    surname2: keyData.formerSurname2 || '',
                    entityName: composeResearchPersonFormerName(keyData),
                  }}
                  onChange={(next) => {
                    const previous = {
                      nameStyle: formerNameStyle,
                      givenName: keyData.formerGivenName || '',
                      givenName2: keyData.formerGivenName2 || '',
                      surname: keyData.formerSurname || '',
                      surname2: keyData.formerSurname2 || '',
                      entityName: composeResearchPersonFormerName(keyData),
                    };
                    const edited = applyOfacNameOrderEdit(previous, next, {
                      detect: true,
                      locked: Boolean(keyData.formerNameStyleLocked),
                      hints: {
                        nationalities: formerOrigin ? [formerOrigin] : nationalityCodes,
                        searchCountry: formerOrigin,
                      },
                    });
                    onKeyDataChange({
                      ...keyData,
                      formerNameStyle: edited.value.nameStyle,
                      formerNameStyleLocked: edited.locked,
                      formerGivenName: edited.value.givenName,
                      formerGivenName2: edited.value.givenName2,
                      formerSurname: edited.value.surname,
                      formerSurname2: edited.value.surname2,
                    } as T);
                  }}
                />
              </>
            ) : null}
            <label className="research-form-panel-label">
              <span>{language === 'es' ? 'Otros alias' : 'Other aliases'}</span>
              <textarea
                {...fieldProps('knownAliases')}
                placeholder={language === 'es' ? 'Apodos u otras grafías, separados por coma' : 'Nicknames or other spellings, comma separated'}
                value={keyData.knownAliases || ''}
                onChange={(event) => patch({ knownAliases: event.target.value } as Partial<T>)}
              />
            </label>
          </div>
          <fieldset className="research-intake-target-type research-intake-age-basis">
            <legend>{language === 'es' ? 'Fecha de nacimiento o rango de edad' : 'Date of birth or age range'}</legend>
            <label>
              <input
                type="radio"
                name="research-age-basis"
                checked={researchPersonAgeBasis(keyData) === 'birthdate'}
                onChange={() => onKeyDataChange(applyResearchAgeBasisChange(keyData, 'birthdate'))}
              />
              {language === 'es' ? 'Fecha de nacimiento' : 'Date of birth'}
            </label>
            <label>
              <input
                type="radio"
                name="research-age-basis"
                checked={researchPersonAgeBasis(keyData) === 'ageRange'}
                onChange={() => onKeyDataChange(applyResearchAgeBasisChange(keyData, 'ageRange'))}
              />
              {language === 'es' ? 'Rango de edad' : 'Age range'}
            </label>
          </fieldset>
          {researchPersonAgeBasis(keyData) === 'birthdate' ? (
            <label className="research-form-panel-label">
              <span>{language === 'es' ? 'Fecha de nacimiento' : 'Date of birth'}</span>
              <input
                type="date"
                {...fieldProps('birthdate')}
                value={keyData.birthdate || ''}
                onChange={(event) => patch({ birthdate: event.target.value } as Partial<T>)}
              />
              <span className="research-form-panel-meta">
                {language === 'es'
                  ? 'Úsala cuando la conozcas. Si no, cambia a rango de edad.'
                  : 'Use this when you know it. Otherwise switch to age range.'}
              </span>
            </label>
          ) : (
            <label className="research-form-panel-label">
              <span>{language === 'es' ? 'Rango de edad aproximado' : 'Approximate age range'}</span>
              <div className="research-form-age-range">
                <input
                  type="text"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  maxLength={3}
                  {...fieldProps('ageFrom')}
                  placeholder={language === 'es' ? 'Desde' : 'From'}
                  aria-label={language === 'es' ? 'Edad mínima aproximada' : 'Approximate age from'}
                  value={ageRangeDraftParts(keyData.ageRange).min}
                  onWheel={(event) => event.currentTarget.blur()}
                  onChange={(event) => patch({
                    ageRange: updateAgeRangeDraft(keyData.ageRange, 'min', event.target.value),
                  } as Partial<T>)}
                />
                <span className="research-form-age-range-sep" aria-hidden="true">–</span>
                <input
                  type="text"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  maxLength={3}
                  {...fieldProps('ageTo')}
                  placeholder={language === 'es' ? 'Hasta' : 'To'}
                  aria-label={language === 'es' ? 'Edad máxima aproximada' : 'Approximate age to'}
                  value={ageRangeDraftParts(keyData.ageRange).max}
                  onWheel={(event) => event.currentTarget.blur()}
                  onChange={(event) => patch({
                    ageRange: updateAgeRangeDraft(keyData.ageRange, 'max', event.target.value),
                  } as Partial<T>)}
                />
              </div>
              {keyData.ageRange ? (
                <button
                  type="button"
                  className="research-form-age-range-clear"
                  onClick={() => patch({ ageRange: '' } as Partial<T>)}
                >
                  {language === 'es' ? 'Quitar filtro de edad' : 'Clear age filter'}
                </button>
              ) : null}
              <span className="research-form-panel-meta">
                {(() => {
                  const committed = parseAgeRange(keyData.ageRange);
                  if (committed) {
                    return language === 'es'
                      ? `Filtro: ${committed.serialized}.`
                      : `Using ages ${committed.serialized}.`;
                  }
                  if (ageRangeIsTooWide(keyData.ageRange)) {
                    return language === 'es'
                      ? 'Ese rango es demasiado amplio y no se enviará como filtro.'
                      : 'That span is too wide and will not be sent as an age filter.';
                  }
                  return language === 'es'
                    ? 'Escribe desde y hasta (por ejemplo 20 y 30). Déjalo vacío si la edad es desconocida.'
                    : 'Type From and To (for example 20 and 30). Leave blank if age is unknown.';
                })()}
              </span>
            </label>
          )}
          <label className="research-form-panel-label">
            <span>{language === 'es' ? 'País de búsqueda' : 'Search country'}</span>
            <select
              aria-label={language === 'es' ? 'País de búsqueda' : 'Search country'}
              {...fieldProps('searchCountry')}
              value={searchCountry}
              onChange={(event) => {
                const next = applyResearchSearchCountryChange(keyData, event.target.value);
                onKeyDataChange(next as T);
              }}
            >
              <option value="">
                {language === 'es' ? 'Selecciona un país' : 'Select a country'}
              </option>
              {countryOptions.map((row) => (
                <option key={`search-${row.value}`} value={row.value}>{row.label}</option>
              ))}
            </select>
            <span className="research-form-panel-meta">
              {language === 'es'
                ? 'Dónde buscar primero (residencia actual). No es el país de origen del apellido ni la nacionalidad. EE. UU. activa directorios y tribunales estadounidenses.'
                : 'Where to look first (current residence). Not surname origin and not nationality — add those under Citizenships. United States turns on US people-finder and courts.'}
            </span>
          </label>
          <div className="research-form-panel-label">
            <span>{language === 'es' ? 'Ciudadanías / nacionalidades' : 'Citizenships / nationalities'}</span>
            {nationalityCodes.length ? (
              <div className="research-intake-chips" role="list">
                {nationalityCodes.map((code) => {
                  const label = countryOptions.find((row) => row.value === code)?.label || code;
                  return (
                    <button
                      key={`nationality-${code}`}
                      type="button"
                      role="listitem"
                      className="dashboard-security-chip dashboard-security-chip-btn"
                      aria-label={language === 'es' ? `Quitar ${label}` : `Remove ${label}`}
                      onClick={() => {
                        const next = applyResearchNationalitiesChange(
                          keyData,
                          nationalityCodes.filter((row) => row !== code),
                        );
                        onKeyDataChange(next as T);
                      }}
                    >
                      {label}
                      <span aria-hidden="true"> ×</span>
                    </button>
                  );
                })}
              </div>
            ) : null}
            <select
              aria-label={language === 'es' ? 'Añadir ciudadanía' : 'Add citizenship'}
              value=""
              onChange={(event) => {
                const code = event.target.value;
                if (!code) return;
                const next = applyResearchNationalitiesChange(keyData, [...nationalityCodes, code]);
                onKeyDataChange(next as T);
              }}
            >
              <option value="">
                {language === 'es' ? 'Añadir ciudadanía…' : 'Add citizenship…'}
              </option>
              {countryOptions
                .filter((row) => !nationalityCodes.includes(row.value))
                .map((row) => (
                  <option key={`citizenship-${row.value}`} value={row.value}>{row.label}</option>
                ))}
            </select>
            <span className="research-form-panel-meta">
              {language === 'es'
                ? 'Añade todas (p. ej. Venezuela y EE. UU.). La ciudadanía de origen no cambia el país de búsqueda ni se confunde con el origen etimológico del apellido.'
                : 'Add all that apply (e.g. Venezuela and United States). Origin citizenship does not change the search country and is not the same as surname etymology.'}
            </span>
          </div>
        </div>
      ) : null}

      {showIdentifiers ? (
        <div className="research-intake-section">
          <h4 className="research-intake-section-title">
            {language === 'es' ? 'Residencia' : 'Residence'}
          </h4>
          <p className="research-form-panel-help">
            {language === 'es'
              ? 'Puede ser distinta de la nacionalidad. Los campos siguen el país de búsqueda o de residencia.'
              : 'Can differ from nationality. Fields follow the search or residence country.'}
          </p>
          <OfacLocationSelect
            language={language}
            includeCountry
            followLayout
            value={locationValue}
            onChange={(next) => {
              const composed = composeResearchPersonPlace({
                ...keyData,
                locationCountry: next.country,
                locationOtherCountry: next.otherCountry,
                locationRegion: next.region,
                locationOtherRegion: next.otherRegion,
                locationCounty: next.county,
                locationOtherCounty: next.otherCounty,
                locationCity: next.city,
                locationOtherCity: next.otherCity,
              }, language);
              patch({
                locationCountry: next.country || '',
                locationOtherCountry: next.otherCountry || '',
                locationRegion: next.region || '',
                locationOtherRegion: next.otherRegion || '',
                locationCounty: next.county || '',
                locationOtherCounty: next.otherCounty || '',
                locationCity: next.city || '',
                locationOtherCity: next.otherCity || '',
                addresses: composed,
              } as Partial<T>);
            }}
          />
          {(() => {
            const residenceCountry = String(
              keyData.locationCountry || keyData.searchCountry || keyData.nationality || '',
            ).trim();
            const layout = ofacAddressLayout(residenceCountry);
            const composeAddress = (partial: Partial<T>) => composeResearchPersonPlace({
              ...keyData,
              ...partial,
            }, language);
            return (
              <>
                {layout.includes('line') ? (
                  <label className="research-form-panel-label">
                    <span>{ofacAddressFieldLabel('line', residenceCountry, language)}</span>
                    <input
                      type="text"
                      {...fieldProps('addressLine')}
                      value={keyData.addressLine || ''}
                      onChange={(event) => patch({
                        addressLine: event.target.value,
                        addresses: composeAddress({ addressLine: event.target.value } as Partial<T>),
                      } as Partial<T>)}
                    />
                  </label>
                ) : null}
                {layout.includes('line2') ? (
                  <label className="research-form-panel-label">
                    <span>{ofacAddressFieldLabel('line2', residenceCountry, language)}</span>
                    <input
                      type="text"
                      {...fieldProps('addressLine2')}
                      value={keyData.addressLine2 || ''}
                      onChange={(event) => patch({
                        addressLine2: event.target.value,
                        addresses: composeAddress({ addressLine2: event.target.value } as Partial<T>),
                      } as Partial<T>)}
                    />
                  </label>
                ) : null}
                {layout.includes('postal') ? (
                  <label className="research-form-panel-label">
                    <span>{ofacAddressFieldLabel('postal', residenceCountry, language)}</span>
                    <input
                      type="text"
                      {...fieldProps('postalCode')}
                      value={keyData.postalCode || ''}
                      onChange={(event) => patch({
                        postalCode: event.target.value,
                        addresses: composeAddress({ postalCode: event.target.value } as Partial<T>),
                      } as Partial<T>)}
                    />
                  </label>
                ) : null}
              </>
            );
          })()}
        </div>
      ) : null}

      {showIdentifiers && (visible.showNationalId || visible.showUsIdentifiers) ? (
        <div className="research-intake-section">
          <h4 className="research-intake-section-title">
            {language === 'es' ? 'Identificadores' : 'Identifiers'}
          </h4>
          {visible.showNationalId ? (
            <div className="research-intake-pair">
              <label className="research-form-panel-label">
                <span>{language === 'es' ? 'Tipo de documento' : 'ID type'}</span>
                <select
                  aria-label={language === 'es' ? 'Tipo de documento' : 'ID type'}
                  {...fieldProps('nationalIdType')}
                  value={keyData.nationalIdType || idTypes[0]?.value || ''}
                  onChange={(event) => patch({ nationalIdType: event.target.value } as Partial<T>)}
                >
                  {idTypes.map((row) => (
                    <option key={row.value} value={row.value}>
                      {language === 'es' ? row.labelEs : row.labelEn}
                    </option>
                  ))}
                </select>
              </label>
              <label className="research-form-panel-label">
                <span>{language === 'es' ? 'Número' : 'ID number'}</span>
                <input
                  type="text"
                  {...fieldProps('nationalId')}
                  value={keyData.nationalId || ''}
                  onChange={(event) => patch({ nationalId: event.target.value } as Partial<T>)}
                />
              </label>
            </div>
          ) : null}
          {visible.showUsIdentifiers ? (
            <>
              <label className="research-form-panel-label">
                <span>{language === 'es' ? 'Número de Seguro Social (se permiten comodines)' : 'Social Security Number (wildcards allowed)'}</span>
                <input
                  type="text"
                  {...fieldProps('ssn')}
                  placeholder={language === 'es' ? 'Ejemplo: 123-45-**** o ***-**-6789' : 'Example: 123-45-**** or ***-**-6789'}
                  value={keyData.ssn || ''}
                  onChange={(event) => patch({ ssn: event.target.value } as Partial<T>)}
                />
              </label>
              <label className="research-form-panel-label">
                <span>{language === 'es' ? 'Número de licencia (DL#)' : 'Driver license number'}</span>
                <input
                  type="text"
                  {...fieldProps('dlNumber')}
                  placeholder={language === 'es' ? 'Opcional' : 'Optional'}
                  value={keyData.dlNumber || ''}
                  onChange={(event) => patch({ dlNumber: event.target.value } as Partial<T>)}
                />
              </label>
            </>
          ) : null}
        </div>
      ) : null}
    </>
  );
}
